import {
  ArrowDownward,
  ArrowUpward,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FilterList as FilterIcon,
  LocalShipping as LocalShippingIcon,
  Refresh as RefreshIcon,
  RestoreFromTrash as RestoreIcon,
  Search as SearchIcon,
  ShoppingCart as ShoppingCartIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Popover,
  Select,
  Skeleton,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import CrossOrderBulkBar from "../../components/backend/orders/CrossOrderBulkBar";
import InlineEditCell from "../../components/backend/orders/InlineEditCell";
import OrderItemDetailsModal from "../../components/backend/orders/OrderItemDetailsModal";
import OrderItemEditModal from "../../components/backend/orders/OrderItemEditModal";
import StatusQuickMenu from "../../components/backend/orders/StatusQuickMenu";
import StatusUpdateModal from "../../components/backend/orders/StatusUpdateModal";
import { adminOrderItemService } from "../../services/adminOrderItemService";
import { getStatusColor } from "../../utils/statusColors";
import {
  ItemStatus,
  OrderItemFlat,
  OrderItemFlatFilters,
} from "../../types/admin";

import AdminPage from "./AdminPage";

const ItemsManagementPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Data
  const [items, setItems] = useState<OrderItemFlat[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [itemStatuses, setItemStatuses] = useState<ItemStatus[]>([]);

  // Pagination & filters
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [filters, setFilters] = useState<OrderItemFlatFilters>({
    pageSize: 20,
    pageNumber: 1,
    sortBy: "orderedat",
    sortDescending: true,
  });
  const [searchText, setSearchText] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Modals
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<OrderItemFlat | null>(null);
  const [deleteConfirmItem, setDeleteConfirmItem] = useState<OrderItemFlat | null>(null);

  // Quick actions
  const [quickActionLoading, setQuickActionLoading] = useState<string | null>(null);
  const [shippedPopoverAnchor, setShippedPopoverAnchor] = useState<HTMLElement | null>(null);
  const [shippedItemId, setShippedItemId] = useState<string | null>(null);
  const [shippedTrackingNumber, setShippedTrackingNumber] = useState("");

  // Snackbar
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  // Load item statuses on mount
  useEffect(() => {
    const loadStatuses = async () => {
      try {
        const stats = await adminOrderItemService.getAvailableItemStatuses();
        setItemStatuses(stats);
      } catch {
        // Non-critical
      }
    };
    loadStatuses();
  }, []);

  // Load items
  const loadRequestRef = useRef(0);
  const loadItems = useCallback(async () => {
    const requestId = ++loadRequestRef.current;
    try {
      setLoading(true);
      const response = await adminOrderItemService.getOrderItems(filters);
      if (requestId === loadRequestRef.current) {
        setItems(response.items);
        setTotalCount(response.totalCount);
      }
    } catch {
      if (requestId === loadRequestRef.current) {
        setSnackbar({
          open: true,
          message: t("admin.orders.errorLoadingOrders"),
          severity: "error",
        });
      }
    } finally {
      if (requestId === loadRequestRef.current) {
        setLoading(false);
      }
    }
  }, [filters, t]);

  // Load items when filters change
  useEffect(() => {
    loadItems();
  }, [loadItems]);

  // Search with debounce
  const handleSearchChange = (value: string) => {
    setSearchText(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setSelectedIds(new Set());
      setFilters((prev) => ({
        ...prev,
        search: value || undefined,
        pageNumber: 1,
      }));
      setPage(0);
    }, 400);
  };

  const handleFilterChange = (key: keyof OrderItemFlatFilters, value: any) => {
    setSelectedIds(new Set());
    setFilters((prev) => ({ ...prev, [key]: value, pageNumber: 1 }));
    setPage(0);
  };

  const handleSort = (key: string) => {
    setSelectedIds(new Set());
    setFilters((prev) => ({
      ...prev,
      sortBy: key,
      sortDescending: prev.sortBy === key ? !prev.sortDescending : true,
    }));
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
    setSelectedIds(new Set());
    setFilters((prev) => ({ ...prev, pageNumber: newPage + 1 }));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newSize = parseInt(event.target.value, 10);
    setRowsPerPage(newSize);
    setPage(0);
    setSelectedIds(new Set());
    setFilters((prev) => ({ ...prev, pageSize: newSize, pageNumber: 1 }));
  };

  const handleRefresh = () => {
    setSelectedIds(new Set());
    loadItems();
  };

  const clearFilters = () => {
    setSearchText("");
    setSelectedIds(new Set());
    setFilters({
      pageSize: rowsPerPage,
      pageNumber: 1,
      sortBy: "orderedat",
      sortDescending: true,
    });
    setPage(0);
  };

  // Selection
  const handleToggleSelect = (itemId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === items.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map((item) => item.id)));
    }
  };

  // Build Map<orderId, Set<itemId>> for CrossOrderBulkBar
  const selectionMap = useMemo(() => {
    const map = new Map<string, Set<string>>();
    items.forEach((item) => {
      if (selectedIds.has(item.id)) {
        if (!map.has(item.orderId)) map.set(item.orderId, new Set());
        map.get(item.orderId)!.add(item.id);
      }
    });
    return map;
  }, [selectedIds, items]);

  // Item actions
  const handleMarkAsOrdered = async (itemId: string) => {
    try {
      setQuickActionLoading(itemId);
      await adminOrderItemService.markAsOrdered(itemId);
      setSnackbar({ open: true, message: t("admin.orders.markedAsOrdered"), severity: "success" });
      loadItems();
    } catch {
      setSnackbar({ open: true, message: t("admin.orders.errorUpdatingItem"), severity: "error" });
    } finally {
      setQuickActionLoading(null);
    }
  };

  const handleMarkAsShippedClick = (event: React.MouseEvent<HTMLElement>, itemId: string) => {
    setShippedPopoverAnchor(event.currentTarget);
    setShippedItemId(itemId);
    setShippedTrackingNumber("");
  };

  const handleMarkAsShippedConfirm = async () => {
    if (!shippedItemId) return;
    try {
      setQuickActionLoading(shippedItemId);
      await adminOrderItemService.markAsShipped(shippedItemId, {
        trackingNumber: shippedTrackingNumber || "",
      });
      setSnackbar({ open: true, message: t("admin.orders.markedAsShipped"), severity: "success" });
      setShippedPopoverAnchor(null);
      setShippedItemId(null);
      loadItems();
    } catch {
      setSnackbar({ open: true, message: t("admin.orders.errorUpdatingItem"), severity: "error" });
    } finally {
      setQuickActionLoading(null);
    }
  };

  const handleInlineFieldSave = async (itemId: string, field: string, value: string) => {
    try {
      await adminOrderItemService.updateOrderItem({
        orderItemId: itemId,
        [field]: value || undefined,
      });
      setSnackbar({ open: true, message: t("admin.orders.itemUpdated"), severity: "success" });
      // Update local state
      setItems((prev) =>
        prev.map((item) =>
          item.id === itemId ? { ...item, [field]: value || undefined } : item
        )
      );
    } catch {
      setSnackbar({ open: true, message: t("admin.orders.errorUpdatingItem"), severity: "error" });
      throw new Error("Failed to update");
    }
  };

  const handleStatusUpdate = async (
    orderItemId: string,
    newStatusId: number,
    notes?: string,
    sendNotification?: boolean
  ) => {
    try {
      await adminOrderItemService.updateItemStatus(orderItemId, newStatusId, notes, sendNotification);
      setSnackbar({ open: true, message: t("admin.orders.statusUpdated"), severity: "success" });
      loadItems();
    } catch {
      setSnackbar({ open: true, message: t("admin.orders.errorUpdatingItem"), severity: "error" });
    }
  };

  const handleEditSave = async (orderItemId: string, updates: any) => {
    try {
      await adminOrderItemService.updateOrderItem({
        orderItemId,
        ...updates,
      });
      setSnackbar({ open: true, message: t("admin.orders.itemUpdated"), severity: "success" });
      setEditModalOpen(false);
      loadItems();
    } catch {
      setSnackbar({ open: true, message: t("admin.orders.errorUpdatingItem"), severity: "error" });
      throw new Error("Failed to update");
    }
  };

  const handleDeleteItem = async () => {
    if (!deleteConfirmItem) return;
    try {
      await adminOrderItemService.deleteOrderItem(deleteConfirmItem.id);
      setSnackbar({ open: true, message: t("admin.orders.itemDeleted"), severity: "success" });
      setDeleteConfirmItem(null);
      loadItems();
    } catch {
      setSnackbar({ open: true, message: t("admin.orders.errorDeletingItem"), severity: "error" });
    }
  };

  const handleRestoreItem = async (itemId: string) => {
    try {
      await adminOrderItemService.restoreOrderItem(itemId);
      setSnackbar({ open: true, message: t("admin.orders.itemRestored"), severity: "success" });
      loadItems();
    } catch {
      setSnackbar({ open: true, message: t("admin.orders.errorRestoringItem"), severity: "error" });
    }
  };

  const handleBulkActionComplete = useCallback(() => {
    setSelectedIds(new Set());
    loadItems();
  }, [loadItems]);


  const hasActiveFilters = !!(
    filters.search ||
    filters.itemStatusId ||
    filters.retailer ||
    filters.hasRetailerOrderId !== undefined ||
    filters.hasRetailerTrackingId !== undefined ||
    filters.retailerOrderId ||
    filters.retailerTrackingId ||
    filters.hasFlightNumber !== undefined ||
    filters.flightNumber ||
    filters.pickUpLocation ||
    filters.includeDeleted
  );

  const sortColumns = [
    { label: t("admin.orders.product"), key: "title" },
    { label: t("admin.orders.orderNumber"), key: "ordernumber" },
    { label: t("admin.orders.customer"), key: "" },
    { label: t("admin.orders.amount"), key: "price" },
    { label: t("admin.orders.qty"), key: "" },
    { label: t("admin.orders.status"), key: "status" },
    { label: t("admin.orders.retailer"), key: "retailer" },
    { label: t("admin.orders.retailerOrderId"), key: "" },
    { label: t("admin.orders.retailerTrackingId"), key: "" },
    { label: t("admin.orders.flightNumber"), key: "flightnumber" },
    { label: t("admin.orders.actions"), key: "" },
  ];

  return (
    <AdminPage>
      <Box sx={{ p: 3, pb: selectedIds.size > 0 ? 10 : 3 }}>
        {/* Page Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h5" fontWeight="bold">
            {t("admin.orders.itemsManagement")}{" "}
            <Typography
              component="span"
              variant="h6"
              color="text.secondary"
            >
              ({totalCount})
            </Typography>
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant={showFilters ? "contained" : "outlined"}
              size="small"
              startIcon={<FilterIcon />}
              onClick={() => setShowFilters(!showFilters)}
            >
              {t("admin.orders.filters")}
            </Button>
            <Tooltip title={t("admin.orders.refresh")}>
              <IconButton onClick={handleRefresh} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Filters Panel */}
        {showFilters && (
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              {/* Item Status Filter */}
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>{t("admin.orders.itemStatusFilter")}</InputLabel>
                <Select
                  value={filters.itemStatusId || ""}
                  onChange={(e) =>
                    handleFilterChange(
                      "itemStatusId",
                      e.target.value === "" ? undefined : Number(e.target.value)
                    )
                  }
                  label={t("admin.orders.itemStatusFilter")}
                >
                  <MenuItem value="">
                    {t("admin.orders.allItemStatuses")}
                  </MenuItem>
                  {itemStatuses.map((status) => (
                    <MenuItem key={status.id} value={status.id}>
                      {status.displayName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Retailer Filter */}
              <TextField
                size="small"
                sx={{ minWidth: 140 }}
                label={t("admin.orders.retailerFilter")}
                value={filters.retailer || ""}
                onChange={(e) =>
                  handleFilterChange("retailer", e.target.value || undefined)
                }
                placeholder="amazon, walmart..."
              />

              {/* Retailer Order ID Filter (has/notset) */}
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>{t("admin.orders.retailerOrderIdFilter")}</InputLabel>
                <Select
                  value={
                    filters.hasRetailerOrderId === true
                      ? "has"
                      : filters.hasRetailerOrderId === false
                        ? "notset"
                        : ""
                  }
                  onChange={(e) => {
                    const val = e.target.value;
                    handleFilterChange(
                      "hasRetailerOrderId",
                      val === "has" ? true : val === "notset" ? false : undefined
                    );
                  }}
                  label={t("admin.orders.retailerOrderIdFilter")}
                >
                  <MenuItem value="">{t("common.all")}</MenuItem>
                  <MenuItem value="has">{t("admin.orders.hasValue")}</MenuItem>
                  <MenuItem value="notset">{t("admin.orders.notSet")}</MenuItem>
                </Select>
              </FormControl>

              {/* Retailer Order ID Search */}
              <TextField
                size="small"
                label={t("admin.orders.retailerOrderId")}
                value={filters.retailerOrderId || ""}
                onChange={(e) =>
                  handleFilterChange("retailerOrderId", e.target.value || undefined)
                }
                sx={{ minWidth: 160 }}
                placeholder="e.g. 112-345..."
              />

              {/* Retailer Tracking ID Filter (has/notset) */}
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>{t("admin.orders.retailerTrackingIdFilter")}</InputLabel>
                <Select
                  value={
                    filters.hasRetailerTrackingId === true
                      ? "has"
                      : filters.hasRetailerTrackingId === false
                        ? "notset"
                        : ""
                  }
                  onChange={(e) => {
                    const val = e.target.value;
                    handleFilterChange(
                      "hasRetailerTrackingId",
                      val === "has" ? true : val === "notset" ? false : undefined
                    );
                  }}
                  label={t("admin.orders.retailerTrackingIdFilter")}
                >
                  <MenuItem value="">{t("common.all")}</MenuItem>
                  <MenuItem value="has">{t("admin.orders.hasValue")}</MenuItem>
                  <MenuItem value="notset">{t("admin.orders.notSet")}</MenuItem>
                </Select>
              </FormControl>

              {/* Retailer Tracking ID Search */}
              <TextField
                size="small"
                label={t("admin.orders.retailerTrackingId")}
                value={filters.retailerTrackingId || ""}
                onChange={(e) =>
                  handleFilterChange("retailerTrackingId", e.target.value || undefined)
                }
                sx={{ minWidth: 160 }}
                placeholder="e.g. TBA123..."
              />

              {/* Flight Number Filter (has/notset) */}
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>{t("admin.orders.flightNumberFilter")}</InputLabel>
                <Select
                  value={
                    filters.hasFlightNumber === true
                      ? "has"
                      : filters.hasFlightNumber === false
                        ? "notset"
                        : ""
                  }
                  onChange={(e) => {
                    const val = e.target.value;
                    handleFilterChange(
                      "hasFlightNumber",
                      val === "has" ? true : val === "notset" ? false : undefined
                    );
                  }}
                  label={t("admin.orders.flightNumberFilter")}
                >
                  <MenuItem value="">{t("common.all")}</MenuItem>
                  <MenuItem value="has">{t("admin.orders.hasValue")}</MenuItem>
                  <MenuItem value="notset">{t("admin.orders.notSet")}</MenuItem>
                </Select>
              </FormControl>

              {/* Flight Number Search */}
              <TextField
                size="small"
                label={t("admin.orders.flightNumber")}
                value={filters.flightNumber || ""}
                onChange={(e) =>
                  handleFilterChange("flightNumber", e.target.value || undefined)
                }
                sx={{ minWidth: 160 }}
                placeholder="e.g. TK123..."
              />

              {/* Pickup Location Filter */}
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>{t("admin.orders.pickupLocation")}</InputLabel>
                <Select
                  value={filters.pickUpLocation || ""}
                  onChange={(e) =>
                    handleFilterChange("pickUpLocation", e.target.value || undefined)
                  }
                  label={t("admin.orders.pickupLocation")}
                >
                  <MenuItem value="">{t("common.all")}</MenuItem>
                  <MenuItem value="Dushanbe">{t("checkout.dushanbe")}</MenuItem>
                  <MenuItem value="Khujand">{t("checkout.khujand")}</MenuItem>
                </Select>
              </FormControl>

              {/* Show Deleted Toggle */}
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={filters.includeDeleted || false}
                    onChange={(e) =>
                      handleFilterChange("includeDeleted", e.target.checked || undefined)
                    }
                  />
                }
                label={t("admin.orders.showDeleted")}
              />

              {hasActiveFilters && (
                <Button size="small" onClick={clearFilters}>
                  {t("admin.orders.clearFilters")}
                </Button>
              )}
            </Box>
          </Paper>
        )}

        {/* Active Filter Chips */}
        {hasActiveFilters && (
          <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
            {filters.search && (
              <Chip
                label={`${t("common.search")}: ${filters.search}`}
                size="small"
                onDelete={() => {
                  setSearchText("");
                  handleFilterChange("search", undefined);
                }}
              />
            )}
            {filters.itemStatusId && (
              <Chip
                label={`${t("admin.orders.itemStatusFilter")}: ${
                  itemStatuses.find((s) => s.id === filters.itemStatusId)
                    ?.displayName || filters.itemStatusId
                }`}
                size="small"
                color="secondary"
                onDelete={() => handleFilterChange("itemStatusId", undefined)}
              />
            )}
            {filters.retailer && (
              <Chip
                label={`${t("admin.orders.retailerFilter")}: ${filters.retailer}`}
                size="small"
                color="secondary"
                onDelete={() => handleFilterChange("retailer", undefined)}
              />
            )}
            {filters.hasRetailerOrderId !== undefined && (
              <Chip
                label={`${t("admin.orders.retailerOrderIdFilter")}: ${
                  filters.hasRetailerOrderId
                    ? t("admin.orders.hasValue")
                    : t("admin.orders.notSet")
                }`}
                size="small"
                color="secondary"
                onDelete={() =>
                  handleFilterChange("hasRetailerOrderId", undefined)
                }
              />
            )}
            {filters.hasRetailerTrackingId !== undefined && (
              <Chip
                label={`${t("admin.orders.retailerTrackingIdFilter")}: ${
                  filters.hasRetailerTrackingId
                    ? t("admin.orders.hasValue")
                    : t("admin.orders.notSet")
                }`}
                size="small"
                color="secondary"
                onDelete={() =>
                  handleFilterChange("hasRetailerTrackingId", undefined)
                }
              />
            )}
            {filters.retailerOrderId && (
              <Chip
                label={`${t("admin.orders.retailerOrderId")}: ${filters.retailerOrderId}`}
                size="small"
                color="secondary"
                onDelete={() => handleFilterChange("retailerOrderId", undefined)}
              />
            )}
            {filters.retailerTrackingId && (
              <Chip
                label={`${t("admin.orders.retailerTrackingId")}: ${filters.retailerTrackingId}`}
                size="small"
                color="secondary"
                onDelete={() => handleFilterChange("retailerTrackingId", undefined)}
              />
            )}
            {filters.hasFlightNumber !== undefined && (
              <Chip
                label={`${t("admin.orders.flightNumberFilter")}: ${
                  filters.hasFlightNumber
                    ? t("admin.orders.hasValue")
                    : t("admin.orders.notSet")
                }`}
                size="small"
                color="secondary"
                onDelete={() =>
                  handleFilterChange("hasFlightNumber", undefined)
                }
              />
            )}
            {filters.flightNumber && (
              <Chip
                label={`${t("admin.orders.flightNumber")}: ${filters.flightNumber}`}
                size="small"
                color="secondary"
                onDelete={() => handleFilterChange("flightNumber", undefined)}
              />
            )}
            {filters.includeDeleted && (
              <Chip
                label={t("admin.orders.showDeleted")}
                size="small"
                color="error"
                onDelete={() => handleFilterChange("includeDeleted", undefined)}
              />
            )}
          </Box>
        )}

        {/* Search Bar */}
        <TextField
          fullWidth
          size="small"
          placeholder={t("admin.orders.searchItemsPlaceholder")}
          value={searchText}
          onChange={(e) => handleSearchChange(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />

        {/* Flat Items Table */}
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={
                        selectedIds.size > 0 && selectedIds.size < items.length
                      }
                      checked={
                        items.length > 0 && selectedIds.size === items.length
                      }
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  {sortColumns.map((col) =>
                    col.key ? (
                      <TableCell key={col.label}>
                        <TableSortLabel
                          active={filters.sortBy === col.key}
                          direction={
                            filters.sortBy === col.key &&
                            !filters.sortDescending
                              ? "asc"
                              : "desc"
                          }
                          onClick={() => handleSort(col.key)}
                          IconComponent={
                            filters.sortBy === col.key &&
                            !filters.sortDescending
                              ? ArrowUpward
                              : ArrowDownward
                          }
                        >
                          {col.label}
                        </TableSortLabel>
                      </TableCell>
                    ) : (
                      <TableCell key={col.label}>{col.label}</TableCell>
                    )
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from({ length: Math.min(rowsPerPage, 10) }).map(
                    (_, i) => (
                      <TableRow key={`skeleton-${i}`}>
                        <TableCell padding="checkbox">
                          <Skeleton variant="circular" width={20} height={20} />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Skeleton variant="rounded" width={40} height={40} />
                            <Skeleton width={120} />
                          </Box>
                        </TableCell>
                        <TableCell><Skeleton width={100} /></TableCell>
                        <TableCell><Skeleton width={80} /></TableCell>
                        <TableCell><Skeleton width={50} /></TableCell>
                        <TableCell><Skeleton width={30} /></TableCell>
                        <TableCell><Skeleton variant="rounded" width={80} height={24} /></TableCell>
                        <TableCell><Skeleton width={60} /></TableCell>
                        <TableCell><Skeleton width={80} /></TableCell>
                        <TableCell><Skeleton width={80} /></TableCell>
                        <TableCell><Skeleton width={100} /></TableCell>
                      </TableRow>
                    )
                  )
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={sortColumns.length + 1} align="center" sx={{ py: 8 }}>
                      <SearchIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
                      <Typography variant="h6" color="text.secondary">
                        {t("admin.orders.noItemsFound")}
                      </Typography>
                      <Typography variant="body2" color="text.disabled" sx={{ mb: 2 }}>
                        {t("admin.orders.tryAdjustingItemFilters")}
                      </Typography>
                      {hasActiveFilters && (
                        <Button variant="outlined" size="small" onClick={clearFilters}>
                          {t("admin.orders.clearFilters")}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item) => (
                    <TableRow
                      key={item.id}
                      selected={selectedIds.has(item.id)}
                      hover
                      sx={item.isDeleted ? { opacity: 0.5, textDecoration: "line-through", bgcolor: "action.hover" } : undefined}
                    >
                      {/* Checkbox */}
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedIds.has(item.id)}
                          onChange={() => handleToggleSelect(item.id)}
                        />
                      </TableCell>

                      {/* Product */}
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 200 }}>
                          {item.image && (
                            <Box
                              component="img"
                              src={item.image}
                              alt={item.title}
                              onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                                e.currentTarget.style.display = "none";
                              }}
                              sx={{
                                width: 40,
                                height: 40,
                                objectFit: "contain",
                                borderRadius: 0.5,
                                flexShrink: 0,
                              }}
                            />
                          )}
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              noWrap
                              sx={{ maxWidth: 220 }}
                              title={item.title}
                            >
                              {item.title}
                            </Typography>
                            {item.isDeleted && (
                              <Chip label={t("admin.orders.deletedBadge")} size="small" color="error" variant="outlined" sx={{ height: 18, fontSize: "0.65rem" }} />
                            )}
                            {item.productId && (
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {item.retailer?.toLowerCase() === "amazon" ? (
                                  <a
                                    href={`https://www.amazon.com/dp/${item.productId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: "inherit" }}
                                  >
                                    {item.productId}
                                  </a>
                                ) : (
                                  item.productId
                                )}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>

                      {/* Order # */}
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "primary.main",
                            cursor: "pointer",
                            "&:hover": { textDecoration: "underline" },
                          }}
                          onClick={() => navigate(`/admin/orders/${item.orderId}`)}
                        >
                          {item.orderNumber}
                        </Typography>
                      </TableCell>

                      {/* Customer */}
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>
                          {item.customerName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {item.customerEmail}
                        </Typography>
                      </TableCell>

                      {/* Price */}
                      <TableCell>
                        <Typography variant="body2">
                          ${(item.price ?? 0).toFixed(2)}
                        </Typography>
                      </TableCell>

                      {/* Qty */}
                      <TableCell align="center">
                        <Typography variant="body2">{item.quantity}</Typography>
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        <StatusQuickMenu
                          itemId={item.id}
                          currentStatusId={item.itemStatusId}
                          currentStatusName={item.itemStatusName}
                          currentStatusDisplayName={item.itemStatusDisplayName}
                          onQuickStatusUpdate={handleStatusUpdate}
                          onOpenFullModal={() => {
                            setSelectedItem(item);
                            setStatusModalOpen(true);
                          }}
                          getStatusColor={getStatusColor}
                        />
                      </TableCell>

                      {/* Retailer */}
                      <TableCell>
                        <Typography variant="body2">
                          {item.retailer || t("admin.orders.na")}
                        </Typography>
                      </TableCell>

                      {/* Retailer Order ID */}
                      <TableCell>
                        <InlineEditCell
                          value={item.retailerOrderId || ""}
                          onSave={(val) =>
                            handleInlineFieldSave(item.id, "retailerOrderId", val)
                          }
                          placeholder={t("admin.orders.notSet")}
                          fontFamily="monospace"
                        />
                      </TableCell>

                      {/* Retailer Tracking ID */}
                      <TableCell>
                        <InlineEditCell
                          value={item.retailerTrackingId || ""}
                          onSave={(val) =>
                            handleInlineFieldSave(item.id, "retailerTrackingId", val)
                          }
                          placeholder={t("admin.orders.notSet")}
                          fontFamily="monospace"
                        />
                      </TableCell>

                      {/* Flight Number */}
                      <TableCell>
                        <InlineEditCell
                          value={item.flightNumber || ""}
                          onSave={(val) =>
                            handleInlineFieldSave(item.id, "flightNumber", val)
                          }
                          placeholder={t("admin.orders.notSet")}
                          fontFamily="monospace"
                        />
                      </TableCell>

                      {/* Actions */}
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                          {/* Mark as Ordered (when status = Order Placed, ID 1) */}
                          {item.itemStatusId === 1 && (
                            <Tooltip title={t("admin.orders.markAsOrdered")}>
                              <span>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleMarkAsOrdered(item.id)}
                                  disabled={quickActionLoading === item.id}
                                >
                                  <ShoppingCartIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          {/* Mark as Shipped (when status = Purchased, ID 2) */}
                          {item.itemStatusId === 2 && (
                            <Tooltip title={t("admin.orders.markAsShipped")}>
                              <span>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={(e) =>
                                    handleMarkAsShippedClick(e, item.id)
                                  }
                                  disabled={quickActionLoading === item.id}
                                >
                                  <LocalShippingIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          <Tooltip title={t("admin.orders.viewDetails")}>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedItem(item);
                                setDetailsModalOpen(true);
                              }}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t("admin.orders.editOrder")}>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedItem(item);
                                setEditModalOpen(true);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {item.isDeleted ? (
                            <Tooltip title={t("admin.orders.restoreItem")}>
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleRestoreItem(item.id)}
                              >
                                <RestoreIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title={t("admin.orders.deleteItem")}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setDeleteConfirmItem(item)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 20, 50, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>

        {/* Cross-Order Bulk Action Bar */}
        {selectedIds.size > 0 && (
          <CrossOrderBulkBar
            selectedItems={selectionMap}
            itemStatuses={itemStatuses}
            onClearSelection={() => setSelectedIds(new Set())}
            onBulkActionComplete={handleBulkActionComplete}
          />
        )}

        {/* Mark as Shipped Popover */}
        <Popover
          open={Boolean(shippedPopoverAnchor)}
          anchorEl={shippedPopoverAnchor}
          onClose={() => {
            setShippedPopoverAnchor(null);
            setShippedItemId(null);
          }}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          transformOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Box sx={{ p: 2, display: "flex", gap: 1, alignItems: "center" }}>
            <TextField
              size="small"
              label={t("admin.orders.trackingNumber")}
              value={shippedTrackingNumber}
              onChange={(e) => setShippedTrackingNumber(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleMarkAsShippedConfirm();
              }}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
            />
            <Button
              variant="contained"
              size="small"
              onClick={handleMarkAsShippedConfirm}
              disabled={quickActionLoading === shippedItemId}
            >
              {t("admin.orders.markAsShipped")}
            </Button>
          </Box>
        </Popover>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={!!deleteConfirmItem}
          onClose={() => setDeleteConfirmItem(null)}
        >
          <DialogTitle>{t("admin.orders.deleteItem")}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t("admin.orders.confirmDeleteItem", {
                title: deleteConfirmItem?.title,
              })}
            </DialogContentText>
            {deleteConfirmItem && (
              <Typography variant="body2" sx={{ mt: 1, fontWeight: "bold" }}>
                {deleteConfirmItem.title}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmItem(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              onClick={handleDeleteItem}
              color="error"
              variant="contained"
            >
              {t("common.delete")}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Status Update Modal */}
        <StatusUpdateModal
          open={statusModalOpen}
          onClose={() => setStatusModalOpen(false)}
          orderItem={
            selectedItem
              ? {
                  id: selectedItem.id,
                  title: selectedItem.title,
                  currentStatusId: selectedItem.itemStatusId,
                  currentStatusName: selectedItem.itemStatusName,
                }
              : null
          }
          onStatusUpdate={handleStatusUpdate}
        />

        {/* Edit Item Modal */}
        <OrderItemEditModal
          open={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          orderItem={selectedItem}
          onSave={handleEditSave}
        />

        {/* Item Details Modal */}
        <OrderItemDetailsModal
          open={detailsModalOpen}
          onClose={() => setDetailsModalOpen(false)}
          orderItemId={selectedItem?.id || ""}
        />

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </AdminPage>
  );
};

export default ItemsManagementPage;
